import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ChannelTextInputComponent } from '@components/channel/channel-text-input/channel-text-input.component';
import { Shipyard } from 'icreate/common/models/shipyard';
import { Vessel } from 'icreate/common/models/vessel';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';
import { GenericErrorStrings } from 'icreate/common/validators/validation-errors/generic';
import { vesselValidator } from 'icreate/common/validators/vessel/vessel-validator';

@Component({
    selector: 'app-new-vessel-dialog',
    templateUrl: './new-vessel-dialog.component.html',
    styleUrls: ['./new-vessel-dialog.component.css'],
    standalone: false,
})
export class NewVesselDialogComponent implements OnInit, AfterViewInit {
    readonly UndefinedOption = CreateOption.UNDEFINED;
    readonly RepurposeOption = CreateOption.REPURPOSE;
    readonly BeginningOption = CreateOption.BEGINNGING;

    readonly NAME = NewVesselVariables.NAME;
    readonly NUMBER = NewVesselVariables.NUMBER;
    readonly DRAWING_ID = NewVesselVariables.DRAWING_ID;
    readonly NUMBER_OF_FCU = NewVesselVariables.NUMBER_OF_FCU;
    readonly NUMBER_OF_OPS = NewVesselVariables.NUMBER_OF_OPS;

    createOption: CreateOption = CreateOption.UNDEFINED;

    @ViewChildren('inputElement') inputElement: QueryList<ChannelTextInputComponent>;

    showVesselData: boolean = false;
    loadingVesselVersion: boolean = false;

    vesselName: string;
    vesselNumber: string;
    drawingId: string;
    numberOps: number = undefined;
    numberFcu: number = undefined;
    selectedShipyard: Shipyard;
    selectedShipyardId: string;
    shipyardList: Shipyard[];

    nameIsRepeated: boolean = false;
    numberIsRepeated: boolean = false;

    isEditing: boolean = false;
    isDuplicating: boolean = false;
    isOwner: boolean = true;
    canEditAll: boolean = false;

    validationErrors: ValidationErrorMap = {};

    referenceVersion: {
        shipyardiApi: string;
        vesseliApi: string;
        version: string;
        vesselName: string;
        vesselNumber: string;
        drawingId: string;
        shipyardName: string;
    };
    hasError: boolean;
    drawingIdRepeated: boolean;

    numberOpsInvalid: boolean;
    numberFcuInvalid: boolean;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            vesselData: Vessel;
            shipyardList: Shipyard[];
            selectedShipyardId: string;
            isOwner: boolean;
            isDuplicate: boolean;
            skipPermissions: boolean;
        },
        private cdr: ChangeDetectorRef,
        private dialogRef: MatDialogRef<NewVesselDialogComponent>
    ) {}

    ngOnInit(): void {
        if (this.data.shipyardList) {
            this.shipyardList = this.data.shipyardList;
        }

        if (this.data.selectedShipyardId) {
            this.selectedShipyardId = this.data.selectedShipyardId;
            if (this.shipyardList) {
                this.selectedShipyard = this.shipyardList.find(shipyard => shipyard.id == this.selectedShipyardId);
            }
        }

        if (this.data.vesselData) {
            this.isEditing = this.data.isDuplicate ? false : true;
            this.isDuplicating = false;
            this.vesselName = this.data.vesselData.name;
            this.vesselNumber = this.data.vesselData.number;
            this.drawingId = this.data.vesselData.drawingId;
            this.numberOps = this.data.vesselData.numberOps;
            this.numberFcu = this.data.vesselData.numberFcu;
            this.isOwner = this.data.isOwner;
        }

        if (this.data.isDuplicate) {
            this.isEditing = false;
            this.isDuplicating = true;
            this.vesselName = this.data.vesselData.name;
            this.vesselNumber = this.data.vesselData.number;
            this.drawingId = this.data.vesselData.drawingId;
            this.numberOps = this.data.vesselData.numberOps;
            this.numberFcu = this.data.vesselData.numberFcu;
            this.isOwner = this.data.isOwner;
            this.dialogHasErrors();
        }

        if (this.data.skipPermissions) this.canEditAll = true;
        this.dialogHasErrors();
        this.cdr.detectChanges();
    }

    ngAfterViewInit(): void {
        if (this.isDuplicating && this.inputElement && this.inputElement.length > 0) {
            for (const input of this.inputElement) {
                this.dialogHasErrors();
                input.inputBlur();
            }
        }
    }

    handleOptionSelected(selectedOption: CreateOption) {
        this.createOption = selectedOption;
    }

    isCreateDisabled(): boolean {
        if (this.isEditing && !this.canEditAll) {
            if (
                this.data.vesselData.name === this.vesselName &&
                this.data.vesselData.number === this.vesselNumber &&
                this.data.vesselData.drawingId === this.drawingId &&
                this.data.vesselData.numberFcu === this.numberFcu &&
                this.data.vesselData.numberOps === this.numberOps
            ) {
                return true;
            }
        }

        return this.hasError || !this.selectedShipyardId;
    }

    dialogHasErrors() {
        const shipyard = this.shipyardList?.find(s => s.id === this.selectedShipyardId);
        const shipyardVessels = shipyard?.vessels || [];

        const numberIsRepeated =
            shipyardVessels.some(v => v.number === this.vesselNumber) &&
            this.data.vesselData?.number !== this.vesselNumber;

        if (this.numberIsRepeated !== numberIsRepeated) {
            this.numberIsRepeated = numberIsRepeated;
        }

        const vesselData = {
            shipyardId: this.selectedShipyardId,
            name: this.vesselName,
            number: this.vesselNumber,
            drawingId: this.drawingId,
            numberOps: this.numberOps,
            numberFcu: this.numberFcu,
        };

        const errors = getValidationErrors(vesselValidator(), vesselData);
        const mappedErrors: { [key: string]: { message: string } } = mapErrors(errors) || {};

        if (this.numberIsRepeated) {
            mappedErrors['number'] = { message: 'vessel.create.dialog.form.number.repeated' };
        }

        if (
            mappedErrors?.numberFcu?.message === GenericErrorStrings.NUMBER_NOT_BELOW ||
            mappedErrors?.numberFcu?.message === GenericErrorStrings.NUMBER_NOT_ABOVE ||
            mappedErrors?.numberFcu?.message === GenericErrorStrings.GENERIC_INVALID_VALUE ||
            this.numberFcuInvalid
        ) {
            mappedErrors['numberFcu'] = { message: 'vessel.create.dialog.form.fcu.range.error' };
        }

        if (
            mappedErrors?.numberOps?.message === GenericErrorStrings.NUMBER_NOT_BELOW ||
            mappedErrors?.numberOps?.message === GenericErrorStrings.NUMBER_NOT_ABOVE ||
            mappedErrors?.numberOps?.message === GenericErrorStrings.GENERIC_INVALID_VALUE ||
            this.numberOpsInvalid
        ) {
            mappedErrors['numberOps'] = { message: 'vessel.create.dialog.form.ops.range.error' };
        }

        this.validationErrors = Object.keys(mappedErrors).length > 0 ? mappedErrors : null;

        const propertiesToCheck = Object.keys(vesselData);
        for (let i = 0; i < propertiesToCheck.length; i++) {
            if (propertiesToCheck[i] in mappedErrors) {
                this.hasError = true;
                return;
            }
        }
        this.hasError = false;
    }

    async createVessel() {
        if (this.isCreateDisabled()) {
            return;
        }

        if (!this.vesselName) {
            const shipyardNameLength = 16 - this.vesselNumber.length;
            if (shipyardNameLength <= 0) this.vesselName = this.vesselNumber;
            else
                this.vesselName = this.selectedShipyard.name.slice(0, shipyardNameLength - 1) + ' ' + this.vesselNumber;
        }

        const vesselData = {
            shipyardId: this.selectedShipyardId,
            name: this.vesselName,
            number: this.vesselNumber,
            drawingId: this.drawingId,
            numberOps: this.numberOps,
            numberFcu: this.numberFcu,
            referenceVersion: this.referenceVersion,
        };
        this.dialogRef.close(vesselData);
    }

    updateVessel() {
        if (this.isCreateDisabled()) {
            return;
        }
        this.dialogRef.close({
            name: this.vesselName,
            number: this.vesselNumber,
            drawingId: this.drawingId,
            numberOps: this.numberOps,
            numberFcu: this.numberFcu,
        });
    }

    clearForm() {
        if (this.canEditAll) return;
        this.dialogRef.close();
    }

    goBack() {
        this.createOption = CreateOption.UNDEFINED;
        if (this.data.selectedShipyardId) {
            this.selectedShipyardId = this.data.selectedShipyardId;
            this.selectedShipyard = this.shipyardList.find(shipyard => shipyard.id == this.selectedShipyardId);
        } else {
            this.selectedShipyardId = null;
            this.selectedShipyard = null;
        }
        this.referenceVersion = null;
        this.vesselName = undefined;
        this.vesselNumber = undefined;
        this.drawingId = undefined;
        this.numberFcu = undefined;
        this.numberOps = undefined;
        this.nameIsRepeated = false;
        this.showVesselData = false;
    }

    updateNumberOps(numberOps: any) {
        const cleaned = numberOps.replace(/[^\d]/g, '');

        this.numberOps = cleaned === '' ? undefined : Number(cleaned);
        this.numberOpsInvalid = numberOps !== cleaned;
    }

    updateNumberFcu(numberFcu: any) {
        const cleaned = numberFcu.replace(/[^\d]/g, '');

        this.numberFcu = cleaned === '' ? undefined : Number(cleaned);
        this.numberFcuInvalid = numberFcu !== cleaned;
    }

    onInputChange(event: { name: string; value: string }) {
        if (event.name === this.NAME) {
            this.vesselName = event.value;
        } else if (event.name === this.NUMBER) {
            this.vesselNumber = event.value;
        } else if (event.name === this.DRAWING_ID) {
            this.drawingId = event.value;
        } else if (event.name === this.NUMBER_OF_FCU) {
            this.updateNumberFcu(event.value);
        } else if (event.name === this.NUMBER_OF_OPS) {
            this.updateNumberOps(event.value);
        }

        this.dialogHasErrors();
    }

    handleShipyardSelected(shipyardId) {
        if (!shipyardId) {
            this.selectedShipyardId = null;
            this.selectedShipyard = null;
        } else {
            this.selectedShipyardId = shipyardId;
            this.selectedShipyard = this.shipyardList.find(shipyard => shipyard.id == this.selectedShipyardId);
        }
    }

    handleSelectedVessel(vessel: Vessel) {
        if (!vessel) {
            this.showVesselData = false;
            return;
        }
        if (vessel && vessel.name && !isNaN(vessel.numberFcu) && !isNaN(vessel.numberOps)) {
            if (!this.selectedShipyard) {
                this.selectedShipyard = this.shipyardList.find(shipyard => shipyard.id == vessel.shipyardId);
                this.selectedShipyardId = this.selectedShipyard.id;
            }

            this.numberFcu = vessel.numberFcu;
            this.numberOps = vessel.numberOps;
            this.showVesselData = true;
        } else {
            this.showVesselData = false;
        }
    }

    handleReferenceSelected(reference: {
        shipyardiApi;
        vesseliApi;
        version;
        vesselName;
        vesselNumber;
        drawingId;
        shipyardName;
    }) {
        if (!reference) {
            this.referenceVersion = null;
            return;
        }
        this.referenceVersion = reference;
    }

    public get validatorFunction() {
        return vesselValidator;
    }
}

export enum CreateOption {
    UNDEFINED = 'undefined',
    REPURPOSE = 'repurpose',
    BEGINNGING = 'beginning',
}

export enum NewVesselVariables {
    NUMBER = 'number',
    NAME = 'name',
    DRAWING_ID = 'drawingId',
    NUMBER_OF_OPS = 'numberOps',
    NUMBER_OF_FCU = 'numberFcu',
}
